import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { webConfig } from '@gitbook/env';

import { useCurrentUser } from '../../state';
import { MagicBellProvider } from './magicbell';
import {
    getMagicBellNotificationsQueryKey,
    getMagicBellTokenQueryKey,
    getMagicBellUnreadCountQueryKey,
} from './queryKeys';
import { useNotificationToasts } from './useNotificationToasts';

/**
 * Wraps the app with the authenticated MagicBell provider when notifications are enabled.
 */
export function NotificationsProvider(props: {
    children: React.ReactElement | React.ReactElement[];
}): React.ReactElement {
    const { children } = props;
    const user = useCurrentUser();
    const queryClient = useQueryClient();
    const previousUserIdRef = React.useRef<string | null>(user?.uid ?? null);

    React.useEffect(() => {
        const previousUserId = previousUserIdRef.current;
        const currentUserId = user?.uid ?? null;

        if (previousUserId && previousUserId !== currentUserId) {
            queryClient.removeQueries({ queryKey: getMagicBellTokenQueryKey(previousUserId) });
            queryClient.removeQueries({
                queryKey: getMagicBellNotificationsQueryKey(previousUserId),
            });
            queryClient.removeQueries({
                queryKey: getMagicBellUnreadCountQueryKey(previousUserId),
            });
        }

        previousUserIdRef.current = currentUserId;
    }, [queryClient, user?.uid]);

    if (!user || !webConfig.magicbell.apiKey) {
        return <>{children}</>;
    }

    return (
        <MagicBellProvider userId={user.uid}>
            <NotificationToasts />
            {children}
        </MagicBellProvider>
    );
}

function NotificationToasts(): null {
    useNotificationToasts();
    return null;
}
